import React, { type ReactElement, useState } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import Alert from 'atom-components/alert';
import Card from 'atom-components/card';
import FormButtonsContainer from 'atom-components/form-buttons-container';
import Heading from 'atom-components/heading';
import Loading from 'atom-components/loading';
import Table from 'atom-components/table-lite/table';
import FullName from 'feature-components/full-name';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import { friendlyMarkerName } from 'helpers/domain-specific/marker.helpers';
import { pluralise } from 'helpers/string.helpers';
import ForceReallocateResponseModal from 'pages/marking/components/force-reallocate-response-modal';
import {
  markersContributingMarksAndMarkItems,
  uniqueMarkerIdsOfMarksAndMarkItems
} from 'pages/marking/helpers/marker.helpers';
import { marksOfResponseParams } from 'pages/marking/helpers/request-params.helpers';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type {
  BaseCustomerUser,
  PartialOrganisationUserWithPartialUser
} from 'services/assist-api/entities/customer-user/types';
import { useTestInstanceResponseMarks } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import type {
  ManageMarkingMark,
  ManageMarkingMarkItem
} from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import { type TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useTestInstanceResponse } from 'services/assist-api/entities/test-instance-response/test-instance-response';
import type { BaseTestInstanceResponse } from 'services/assist-api/entities/test-instance-response/types';
import { useTestInstance } from 'services/assist-api/entities/test-instance/test-instance';
import type { BaseTestInstance } from 'services/assist-api/entities/test-instance/types';
import { useTestRubricItems } from 'services/assist-api/entities/test-rubric-item/hooks';
import type { BaseTestRubricItem } from 'services/assist-api/entities/test-rubric-item/types';
import { useTestRubrics } from 'services/assist-api/entities/test-rubric/hooks';
import { AssistQueryClient } from 'services/assist-api/query-client';
import { isInitialLoad } from 'utils/check-query-status';
import { finishedMarksOfResponseCount } from './deallocation-results.helpers';
import ForceReallocateResponseButton from './force-reallocate-response-button';
import Mark from './mark';
import MarkItem from './mark-item';
import styles from './index.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  responseId: number;
  allocatedMarkerId: number;
  allocatedOrganisationUser: PartialOrganisationUserWithPartialUser | undefined;
  onForceReallocate: () => void;
};

export default function DeallocationResultsModal({
  isOpen,
  onClose,
  responseId,
  allocatedMarkerId,
  allocatedOrganisationUser,
  onForceReallocate
}: Props): ReactElement {
  const [showForceReallocateModal, setShowForceReallocateModal] = useState<boolean>(false);

  // Fetch marks and mark items linked to the response to determine if we can deallocate the marker.
  const testInstanceResponseMarksQuery = useTestInstanceResponseMarks<ManageMarkingMark>(
    marksOfResponseParams(responseId),
    // Only run this query when the the modal is open for performance reasons:
    { enabled: isOpen }
  );
  const marksOfResponse = testInstanceResponseMarksQuery.data?.json || [];

  // Get the marks (with mark items) that the marker has contributed to (which would block
  // deallocation).
  const contributingMarks: ManageMarkingMark[] = markersContributingMarksAndMarkItems(
    allocatedMarkerId,
    marksOfResponse
  );

  const contributingMarkerIds = uniqueMarkerIdsOfMarksAndMarkItems(contributingMarks);

  // Fetch the markers that have contributed to the marks and mark items:
  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      {
        filters: { id: contributingMarkerIds },
        groups: ['testinstanceresponsemarker-user'],
        include: 'users'
      },
      { enabled: !!contributingMarkerIds.length }
    );

  // Fetch the organisation users of those contributing markers:
  const organisationUserMarkerIds =
    testInstanceResponseMarkersQuery?.data?.json.map((marker) => marker.user.id) || [];
  const organisationUsersQuery = useCustomerUsers<BaseCustomerUser>(
    {
      filters: { id: organisationUserMarkerIds }
    },
    { enabled: !!organisationUserMarkerIds.length }
  );

  // Fetch the response so that we can determine the test instance:
  const testInstanceResponseQuery = useTestInstanceResponse<BaseTestInstanceResponse>(responseId);

  // Fetch the test instance so that we can determine the test:
  const testInstanceQuery = useTestInstance<BaseTestInstance>(
    testInstanceResponseQuery.data?.json.testInstance.id
  );

  // Fetch the rubrics so that we can determine the rubric items:
  const testRubricTestId = testInstanceQuery.data?.json.test.id;
  const testRubricsQuery = useTestRubrics(
    {
      filters: { tests: testRubricTestId },
      groups: ['testrubric-mediaobjects', 'mediaobject-list'],
      include: 'mediaObjects'
    },
    { enabled: !!testRubricTestId }
  );

  // Fetch the rubric items so that we can determine information about what the mark items scored:
  const testRubricIds = testRubricsQuery.data?.json.map((rubric) => rubric.id) || [];
  const testRubricItemsQuery = useTestRubricItems<BaseTestRubricItem>(
    {
      filters: { testRubrics: testRubricIds },
      groups: ['testrubricitem-displayName']
    },
    { enabled: !!testRubricIds.length }
  );

  // Do not enforce loading of rubric items here. In the case of the test not having a rubric we
  // still want to load the results:
  const isLoading = isInitialLoad(
    testInstanceResponseMarksQuery,
    testInstanceResponseMarkersQuery,
    organisationUsersQuery,
    testInstanceQuery,
    testInstanceResponseQuery,
    testRubricsQuery
  );

  const isError =
    testInstanceResponseMarksQuery.isError ||
    testInstanceResponseMarkersQuery.isError ||
    organisationUsersQuery.isError ||
    testInstanceQuery.isError ||
    testInstanceResponseQuery.isError ||
    testRubricsQuery.isError;

  const markers = testInstanceResponseMarkersQuery.data?.json || [];
  const organisationUsers = organisationUsersQuery.data?.json || [];

  function markerFromActiveMarkerId(
    activeMarkerId?: number
  ): TestInstanceResponseMarkerWithCustomerUser | undefined {
    return markers.find((m) => m.id === activeMarkerId);
  }

  function organisationUserFromActiveMarkerId(
    activeMarkerId?: number
  ): BaseCustomerUser | undefined {
    const marker = markerFromActiveMarkerId(activeMarkerId);
    return organisationUsers.find((ou) => ou.id === marker?.user.id);
  }

  function rubricItemOptionForMarkItem(
    markItem: ManageMarkingMarkItem
  ): BaseTestRubricItem | undefined {
    return testRubricItemsQuery.data?.json.find((tri) => tri.id === markItem.testRubricItem.id);
  }

  const finishedMarksCount = finishedMarksOfResponseCount(allocatedMarkerId, marksOfResponse);

  return (
    <Modal isOpen={isOpen} size="lg" scrollable>
      <ModalHeader toggle={onClose}>Deallocation Results</ModalHeader>
      <ModalBody>
        {showForceReallocateModal && (
          <ForceReallocateResponseModal
            isOpen={showForceReallocateModal}
            onClose={(): void => setShowForceReallocateModal(false)}
            responseId={responseId}
            allocatedMarkerId={allocatedMarkerId}
            allocatedOrganisationUser={allocatedOrganisationUser}
            onForceReallocate={onForceReallocate}
          />
        )}
        {isLoading && <Loading overlay={false} />}
        {!isLoading && isError && (
          <LoadingErrorAlert
            queries={[
              testInstanceResponseMarkersQuery,
              organisationUsersQuery,
              testInstanceQuery,
              testInstanceResponseQuery,
              testRubricsQuery
            ]}
          />
        )}
        {!isLoading && !isError && (
          <>
            <div>
              {contributingMarks.length === 0 && (
                <Alert variant="success">
                  All marks of <b>response #{responseId}</b> that{' '}
                  <FullName organisationUser={allocatedOrganisationUser} bringAttentionTo />{' '}
                  contributed to have been deleted. There is no marking work blocking deallocation.
                </Alert>
              )}
              {contributingMarks.length > 0 && (
                <p>
                  Cannot deallocate{' '}
                  <FullName organisationUser={allocatedOrganisationUser} bringAttentionTo /> from{' '}
                  <b>response #{responseId}</b>.
                </p>
              )}
              {finishedMarksCount > 0 && (
                <Alert variant="danger">
                  Since {finishedMarksCount === 1 ? 'a' : 'some'}{' '}
                  {pluralise('mark', finishedMarksCount)} {pluralise('is', finishedMarksCount)}{' '}
                  finished, {friendlyMarkerName(allocatedOrganisationUser)} cannot be deallocated
                  and the Force Reallocate Response option is not available. You can either delete
                  the {pluralise('mark', finishedMarksCount)} or change to a draft status to enable
                  force reallocation.
                </Alert>
              )}
              {contributingMarks.length > 0 && (
                <Alert variant="warning">
                  Deallocation cannot be completed until the{' '}
                  {pluralise(
                    'mark',
                    contributingMarks.filter((m) => m.activeMarker?.id === allocatedMarkerId).length
                  )}{' '}
                  assigned to {friendlyMarkerName(allocatedOrganisationUser)}{' '}
                  {pluralise(
                    'has',
                    contributingMarks.filter((m) => m.activeMarker?.id === allocatedMarkerId).length
                  )}{' '}
                  been deleted or assigned to another marker and their items have been rescored or
                  unscored. This can be done manually or via the Force Reallocate Response option.
                </Alert>
              )}
              {contributingMarks.length > 0 && (
                <div>
                  <Heading level={2}>Marks</Heading>
                  <ul className={styles.markList}>
                    {contributingMarks.map((mark) => (
                      <li key={mark.id}>
                        <Card>
                          <Card.Header>
                            <Mark
                              responseId={responseId}
                              mark={mark}
                              allocatedMarkerId={allocatedMarkerId}
                              activeMarker={markerFromActiveMarkerId(mark.activeMarker?.id)}
                              organisationUser={organisationUserFromActiveMarkerId(
                                mark.activeMarker?.id
                              )}
                              onDeleteMark={async (): Promise<void> => {
                                await AssistQueryClient.invalidateQueries([
                                  'test-instance-response-marks'
                                ]);

                                // Trigger the marks query to run again now that the cache has been
                                // cleared. This will remove the deleted marks from the list:
                                await testInstanceResponseMarksQuery.refetch();
                              }}
                            />
                          </Card.Header>
                          <Card.Body>
                            <>
                              {mark.markItems?.length === 0 && <p>No scored mark items.</p>}
                              {mark.markItems && mark.markItems.length > 0 && (
                                <div>
                                  <Table>
                                    <Table.Head>
                                      <Table.Row>
                                        <Table.Header />
                                        <Table.Header>Mark Item ID</Table.Header>
                                        <Table.Header>Rubric Item </Table.Header>
                                        <Table.Header>Active Marker</Table.Header>
                                      </Table.Row>
                                    </Table.Head>
                                    <Table.Body>
                                      {mark.markItems.map((markItem) => (
                                        <MarkItem
                                          key={markItem.id}
                                          markItem={markItem}
                                          rubricItem={rubricItemOptionForMarkItem(markItem)}
                                          allocatedMarkerId={allocatedMarkerId}
                                          activeMarker={markerFromActiveMarkerId(
                                            markItem.testInstanceResponseMarker?.id
                                          )}
                                          organisationUser={organisationUserFromActiveMarkerId(
                                            markItem.testInstanceResponseMarker?.id
                                          )}
                                        />
                                      ))}
                                    </Table.Body>
                                  </Table>
                                </div>
                              )}
                            </>
                          </Card.Body>
                        </Card>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <FormButtonsContainer>
              <ForceReallocateResponseButton
                onClick={(): void => setShowForceReallocateModal(true)}
                allocatedMarkerId={allocatedMarkerId}
                marksOfResponse={marksOfResponse}
              />
              <Button color="secondary" onClick={onClose}>
                {contributingMarks.length ? 'Manually Make Changes' : 'Close'}
              </Button>
            </FormButtonsContainer>
          </>
        )}
      </ModalBody>
    </Modal>
  );
}
